import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import {
  ArrowDownCircle,
  ArrowUpCircle,
  CreditCard,
  Landmark,
  PieChart,
  Plus,
  Target,
  Wallet,
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { m, useReducedMotion } from 'motion/react';
import {
  formatBRL,
  monthName,
  type CategorySlice,
  type DashboardAccount,
  type DashboardResponse,
  type IncomeExpensePoint,
  type MonthPoint,
} from '@lira/shared';
import { apiGet } from '@/lib/api';
import { errorMessage } from '@/lib/errorMessage';
import { cn } from '@/lib/cn';
import { useMonthStore } from '@/stores/month';
import { usePrivacyStore } from '@/stores/privacy';
import { useCountUp } from '@/hooks/useCountUp';
import { CategoryIcon, MoneyText, Skeleton } from '@/components/ui';
import { DonutChart, GaugeChart, GroupedBarChart, LineChart } from '@/components/charts';
import { ExpenseAgendaCard } from './ExpenseAgendaCard';

const EASE = [0.16, 1, 0.3, 1] as const;

/** 'YYYY-MM' → 'jul' (label curto de eixo). */
function shortMonth(ym: string): string {
  return monthName(ym).slice(0, 3);
}

// --- Hero cards (count-up) --------------------------------------------------

function CountUpMoney({
  cents,
  delay,
  className,
}: {
  cents: number;
  delay: number;
  className?: string;
}) {
  const hidden = usePrivacyStore((s) => s.hidden);
  const value = useCountUp(cents, delay);
  return (
    <p className={cn('tabular-nums', className)} aria-label={hidden ? 'Valor oculto' : undefined}>
      {hidden ? '••••••' : formatBRL(value)}
    </p>
  );
}

function HeroCard({
  label,
  icon: Icon,
  tint,
  cents,
  details,
  delay,
  to,
}: {
  label: string;
  icon: LucideIcon;
  tint: string;
  cents: number;
  details?: Array<{ label: string; cents: number }>;
  delay: number;
  to: string;
}) {
  const navigate = useNavigate();
  const reduce = useReducedMotion();
  return (
    <m.button
      type="button"
      onClick={() => navigate(to)}
      whileTap={reduce ? undefined : { scale: 0.97 }}
      transition={{ duration: 0.12, ease: EASE }}
      className="flex min-h-32 items-center gap-3 rounded-xl bg-surface p-4 text-left shadow-card transition-shadow hover:shadow-hover xl:gap-4 xl:p-5"
    >
      <span
        className={cn('grid size-11 shrink-0 place-items-center rounded-full text-white lg:size-10 xl:size-13', tint)}
      >
        <Icon className="size-5" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-[13px] text-muted">{label}</p>
        <CountUpMoney cents={cents} delay={delay} className="text-xl font-bold text-ink" />
        {details && (
          <div className="mt-1.5 space-y-0.5 border-t border-border/60 pt-1.5 text-[11px] text-muted">
            {details.map((detail) => (
              <p key={detail.label} className="flex min-w-0 items-baseline justify-between gap-1.5">
                <span className="truncate">{detail.label}</span>
                <MoneyText cents={detail.cents} className="shrink-0 text-[11px] font-semibold" />
              </p>
            ))}
          </div>
        )}
      </div>
    </m.button>
  );
}

// --- Blocos de apoio --------------------------------------------------------

function WidgetCard({
  title,
  action,
  footerAction,
  children,
  className,
}: {
  title?: string;
  action?: ReactNode;
  footerAction?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('rounded-xl bg-surface p-5 shadow-card', className)}>
      {(title || action) && (
        <div className="mb-4 flex items-center justify-between gap-2">
          {title && <h2 className="text-sm font-semibold text-ink">{title}</h2>}
          {action}
        </div>
      )}
      {children}
      {footerAction && <div className="mt-auto pt-4">{footerAction}</div>}
    </div>
  );
}

function VerMais({ to, label = 'Ver mais' }: { to: string; label?: string }) {
  return (
    <Link
      to={to}
      className="text-xs font-semibold uppercase tracking-wide text-primary-text transition-colors hover:text-primary-text-hover"
    >
      {label}
    </Link>
  );
}

function WidgetEmpty({ icon: Icon, message }: { icon: LucideIcon; message: string }) {
  return (
    <div className="grid place-items-center gap-2 py-8 text-center">
      <span className="grid size-11 place-items-center rounded-full bg-primary-weak text-primary">
        <Icon className="size-5" />
      </span>
      <p className="max-w-xs text-sm text-muted">{message}</p>
    </div>
  );
}

function Reveal({
  index = 0,
  className,
  children,
}: {
  index?: number;
  className?: string;
  children: ReactNode;
}) {
  const reduce = useReducedMotion();
  return (
    <m.div
      className={className}
      initial={reduce ? false : { opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: reduce ? 0 : 0.25, delay: reduce ? 0 : index * 0.05, ease: EASE }}
    >
      {children}
    </m.div>
  );
}

// --- Widgets ----------------------------------------------------------------

function ExpensesByCategoryCard({ data }: { data: CategorySlice[] }) {
  const total = data.reduce((sum, c) => sum + c.totalCents, 0);
  const slices = data.map((c) => ({
    id: c.categoryId,
    label: c.name,
    value: c.totalCents,
    color: c.color,
  }));

  return (
    <WidgetCard
      title="Despesas por categoria"
      footerAction={<VerMais to="/relatorios" />}
      className="flex min-h-[24rem] flex-col lg:min-h-[30rem]"
    >
      {data.length === 0 || total === 0 ? (
        <WidgetEmpty icon={PieChart} message="Nenhuma despesa registrada neste mês." />
      ) : (
        <div className="flex flex-1 flex-col items-center justify-center gap-5">
          <div className="w-full max-w-[17rem]">
            <DonutChart
              slices={slices}
              centerLabel={<MoneyText cents={total} bold />}
              centerSub="Total"
            />
          </div>
          <ul className="w-full space-y-2.5 lg:hidden">
            {data.slice(0, 6).map((c) => {
              const pct = Math.round((c.totalCents / total) * 100);
              return (
                <li key={c.categoryId} className="flex items-center gap-3">
                  <CategoryIcon icon={c.icon} color={c.color} size={28} />
                  <span className="min-w-0 flex-1 truncate text-sm text-ink">{c.name}</span>
                  <span className="shrink-0 text-xs tabular-nums text-muted">{pct}%</span>
                  <MoneyText cents={c.totalCents} className="shrink-0 text-sm" />
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </WidgetCard>
  );
}

function BalanceHistoryCard({ data }: { data: MonthPoint[] }) {
  const points = data.map((p) => ({ label: shortMonth(p.month), value: p.balanceCents }));
  return (
    <WidgetCard
      title="Balanço (Receitas - Despesas) nos últimos 6 meses"
      footerAction={<VerMais to="/relatorios" />}
      className="flex min-h-[24rem] flex-col lg:min-h-[30rem]"
    >
      {points.length === 0 ? (
        <WidgetEmpty icon={Wallet} message="Sem histórico de saldo ainda." />
      ) : (
        <div className="flex flex-1 items-center">
          <LineChart points={points} />
        </div>
      )}
    </WidgetCard>
  );
}

function IncomeVsExpenseCard({ data }: { data: IncomeExpensePoint[] }) {
  const groups = data.map((p) => ({
    label: shortMonth(p.month),
    incomeCents: p.incomeCents,
    expenseCents: p.expenseCents,
  }));
  return (
    <WidgetCard
      title="Receitas x Despesas"
      action={
        <div className="flex items-center gap-3 text-xs text-muted">
          <span className="flex items-center gap-1.5">
            <span className="inline-block size-2.5 rounded-full bg-income" />
            Receitas
          </span>
          <span className="flex items-center gap-1.5">
            <span className="inline-block size-2.5 rounded-full bg-expense" />
            Despesas
          </span>
        </div>
      }
    >
      {groups.length === 0 ? (
        <WidgetEmpty icon={ArrowUpCircle} message="Sem lançamentos nos últimos meses." />
      ) : (
        <GroupedBarChart data={groups} />
      )}
    </WidgetCard>
  );
}

function SavingsGaugeCard({ percent }: { percent: number }) {
  const pct = Number.isFinite(percent) ? percent : 0;
  const rounded = Math.round(pct);
  const color =
    pct >= 20
      ? 'var(--color-income)'
      : pct >= 1
        ? 'var(--color-alert)'
        : 'var(--color-expense)';

  let message: string;
  if (pct > 0) message = `Você economizou ${rounded}% do que ganhou este mês.`;
  else if (pct === 0) message = 'Você não economizou neste mês.';
  else message = 'Você gastou mais do que ganhou este mês.';

  const tip =
    pct >= 20
      ? 'Ótimo ritmo! Continue guardando parte da sua renda.'
      : pct >= 1
        ? 'Dá para melhorar: revise pequenos gastos supérfluos.'
        : 'Atenção: reveja suas despesas para reequilibrar o mês.';

  return (
    <WidgetCard title="Taxa de economia">
      <GaugeChart percent={pct} color={color} />
      <p className="mt-3 text-center text-sm font-medium text-ink">{message}</p>
      <p className="mt-1 text-center text-xs text-muted">{tip}</p>
    </WidgetCard>
  );
}

function PendingRow({
  label,
  cents,
  tone,
  icon: Icon,
}: {
  label: string;
  cents: number;
  tone: 'income' | 'expense';
  icon: LucideIcon;
}) {
  const isExpense = tone === 'expense';
  return (
    <div className="flex items-center gap-3">
      <span
        className={cn(
          'grid size-9 shrink-0 place-items-center rounded-full',
          isExpense ? 'bg-expense-weak text-expense' : 'bg-income-weak text-income',
        )}
      >
        <Icon className="size-5" />
      </span>
      <span className="flex-1 text-sm text-ink">{label}</span>
      <MoneyText
        cents={cents}
        bold
        className={cn('text-sm', isExpense ? 'text-expense' : 'text-income')}
      />
    </div>
  );
}

function PendingsCard({
  pendingExpenseCents,
  pendingIncomeCents,
}: {
  pendingExpenseCents: number;
  pendingIncomeCents: number;
}) {
  return (
    <WidgetCard title="Pendências do mês">
      <div className="space-y-3">
        <PendingRow
          label="Despesas pendentes"
          cents={pendingExpenseCents}
          tone="expense"
          icon={ArrowDownCircle}
        />
        <PendingRow
          label="Receitas pendentes"
          cents={pendingIncomeCents}
          tone="income"
          icon={ArrowUpCircle}
        />
      </div>
    </WidgetCard>
  );
}

function AccountsMiniCard({ accounts }: { accounts: DashboardAccount[] }) {
  return (
    <WidgetCard title="Contas" action={<VerMais to="/contas" label="Ver todas" />}>
      {accounts.length === 0 ? (
        <WidgetEmpty icon={Landmark} message="Nenhuma conta cadastrada." />
      ) : (
        <ul className="space-y-3">
          {accounts.slice(0, 5).map((a) => (
            <li key={a.id} className="flex items-center gap-3">
              <CategoryIcon icon={a.icon} color={a.color} fallbackText={a.name} size={32} />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm text-ink">{a.name}</p>
                <p className="flex items-center gap-1 text-xs text-muted">
                  Previsto:
                  <MoneyText cents={a.predictedCents} colorBySign className="text-xs" />
                </p>
              </div>
              <MoneyText cents={a.currentCents} colorBySign bold className="shrink-0 text-sm" />
            </li>
          ))}
        </ul>
      )}
    </WidgetCard>
  );
}

function PlanningCard() {
  const navigate = useNavigate();
  return (
    <WidgetCard title="Planejamento">
      <div className="grid place-items-center gap-3 py-6 text-center">
        <span className="grid size-12 place-items-center rounded-full bg-primary-weak text-primary">
          <Target className="size-6" />
        </span>
        <div>
          <p className="text-sm font-semibold text-ink">Sem planejamento</p>
          <p className="mt-1 max-w-xs text-xs text-muted">
            Crie orçamentos para acompanhar seus limites de gasto por categoria.
          </p>
        </div>
        <button
          type="button"
          onClick={() => navigate('/orcamentos')}
          className="inline-flex h-9 items-center gap-1.5 rounded-full bg-primary px-4 text-xs font-semibold uppercase tracking-wide text-white transition-colors hover:bg-primary-hover"
        >
          <Plus className="size-4" />
          Criar orçamento
        </button>
      </div>
    </WidgetCard>
  );
}

// --- Conteúdo (remonta por mês → re-dispara count-up + reveals) -------------

function DashboardContent({ data }: { data: DashboardResponse }) {
  return (
    <div data-testid="dashboard-content" className="space-y-3">
      <div data-testid="dashboard-kpis" className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <HeroCard
          label="Saldo atual"
          icon={Wallet}
          tint="bg-balance"
          cents={data.balance.currentCents}
          delay={0}
          to="/contas"
        />
        <HeroCard
          label="Receitas"
          icon={ArrowUpCircle}
          tint="bg-income"
          cents={data.totals.incomeCents + data.totals.pendingIncomeCents}
          details={[
            { label: 'Recebidas', cents: data.totals.incomeCents },
            { label: 'A receber', cents: data.totals.pendingIncomeCents },
          ]}
          delay={0.08}
          to="/transacoes?type=income"
        />
        <HeroCard
          label="Despesas"
          icon={ArrowDownCircle}
          tint="bg-expense"
          cents={data.totals.expenseCents + data.totals.pendingExpenseCents}
          details={[
            { label: 'Pagas', cents: data.totals.expenseCents },
            { label: 'A pagar', cents: data.totals.pendingExpenseCents },
          ]}
          delay={0.16}
          to="/transacoes?type=expense"
        />
        <HeroCard
          label="Cartão de crédito"
          icon={CreditCard}
          tint="bg-cards"
          cents={data.cardsDueCents}
          delay={0.24}
          to="/cartoes"
        />
      </div>

      <Link
        to="/relatorios"
        className="inline-flex min-h-11 items-center gap-1 text-sm font-semibold text-primary-text transition-colors hover:text-primary-text-hover"
      >
        Meu desempenho <span aria-hidden>›</span>
      </Link>

      {/* As grades remontam por mês para redesenhar os gráficos sem alterar
          a origem financeira dos KPIs ou o contexto global de período. */}
      <div key={data.month} className="space-y-4">
        <div data-testid="dashboard-primary-charts" className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <Reveal index={0}>
            <div data-testid="dashboard-expenses-chart">
              <ExpensesByCategoryCard data={data.expensesByCategory} />
            </div>
          </Reveal>
          <Reveal index={1}>
            <div data-testid="dashboard-balance-chart">
              <BalanceHistoryCard data={data.balanceHistory} />
            </div>
          </Reveal>
        </div>

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <Reveal index={2}>
            <IncomeVsExpenseCard data={data.incomeVsExpense} />
          </Reveal>
          <Reveal index={3}>
            <SavingsGaugeCard percent={data.savingsPercent} />
          </Reveal>
          <Reveal index={4}>
            <PendingsCard
              pendingExpenseCents={data.totals.pendingExpenseCents}
              pendingIncomeCents={data.totals.pendingIncomeCents}
            />
          </Reveal>
          <Reveal index={5}>
            <AccountsMiniCard accounts={data.accounts} />
          </Reveal>
          <Reveal index={6}>
            <PlanningCard />
          </Reveal>
        </div>
      </div>

      <Reveal index={7}>
        <ExpenseAgendaCard month={data.month} />
      </Reveal>
    </div>
  );
}

function DashboardSkeleton() {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {[0, 1, 2, 3].map((i) => (
          <Skeleton key={i} variant="card" className="h-[76px]" />
        ))}
      </div>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Skeleton variant="card" className="h-64 lg:col-span-2" />
        {[0, 1, 2, 3, 4].map((i) => (
          <Skeleton key={i} variant="card" className="h-56" />
        ))}
      </div>
    </div>
  );
}

// --- Página -----------------------------------------------------------------

export function DashboardPage() {
  const month = useMonthStore((s) => s.month);
  const query = useQuery({
    queryKey: ['dashboard', month],
    queryFn: () => apiGet<DashboardResponse>(`/dashboard?month=${month}`),
    // Mantém o mês anterior visível enquanto o novo carrega: sem flash de
    // skeleton ao trocar de mês e o count-up pode animar do valor antigo→novo.
    placeholderData: keepPreviousData,
  });

  return (
    <section className="space-y-5">
      <h1 className="sr-only">Dashboard</h1>
      {query.isLoading && <DashboardSkeleton />}

      {/* Mostra o erro mesmo com dados anteriores em tela (keepPreviousData):
          nunca engole uma falha de troca de mês sob o cabeçalho do novo mês. */}
      {query.isError && (
        <p className="rounded-lg bg-expense-weak px-4 py-6 text-center text-sm text-expense">
          {errorMessage(query.error)}
        </p>
      )}

      {query.data && <DashboardContent data={query.data} />}
    </section>
  );
}
